import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { lightThemeTokens, darkThemeTokens } from './tokens';
import { ThemeMode, ThemeState } from './types';
import { reportUiError } from '../error/reporting';

/**
 * Interface for theme storage configuration options
 */
export interface ThemeStorageConfig {
  /** The storage implementation to use */
  storage: ReturnType<typeof createJSONStorage>;
  /** Optional function to determine initial theme mode when no persisted value exists */
  getInitialMode?: () => ThemeMode;
  /** Error reporter */
  reportErrorFn: (error: unknown, context?: Record<string, unknown>) => void;
}

/**
 * Creates a theme store with a custom storage implementation.
 * This allows for using different storage backends on different platforms.
 *
 * @param options - Storage configuration
 * @returns A Zustand store with theme state
 */
export const createThemeStore = (options: ThemeStorageConfig) => {
  const { storage, reportErrorFn } = options; // Removed getInitialMode from destructuring

  return create<ThemeState>()(
    persist(
      (set) => {
        // Use the provided initial mode function or default to light
        const initialMode = options.getInitialMode ? options.getInitialMode() : ThemeMode.Light;

        return {
          mode: initialMode,
          colors: initialMode === ThemeMode.Light ? lightThemeTokens : darkThemeTokens,

          setThemeMode: (mode: ThemeMode) => {
            const newColors = mode === ThemeMode.Light ? lightThemeTokens : darkThemeTokens;
            set({ mode, colors: newColors });
          },
        };
      },
      {
        name: 'theme-preferences',
        storage,
        partialize: (state) => ({
          mode: state.mode,
        }),
        onRehydrateStorage: () => {
          return (persistedState, error) => {
            if (error) {
              // Use the reporter if provided
              reportUiError(reportErrorFn, 'theme.onRehydrateStorage', error);
              return;
            }
            if (persistedState) {
              // We need to recreate the colors after hydration
              // since it's not persisted directly
              persistedState.colors =
                persistedState.mode === ThemeMode.Light ? lightThemeTokens : darkThemeTokens;
            }
          };
        },
      }
    )
  );
};
